import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { environment } from '../../environments/environment';
import { GetUsersByAdminQuery } from '../interface/users-v2.interface';
import { ExcelInfoToUpload, PayloadUpdateTmpUser, PayloadUpdateUser, PhoneDetail } from '../interface/users.interface';
import { DataService } from './data.service';

export enum UserStatus {
  active = 'active',
  inactive = 'inactive',
  deleted = 'deleted',
}
@Injectable()
export class UserService extends DataService {
  page = 1;
  rowsPerPage = 10;

  constructor(private http: HttpClient, protected injector: Injector, protected store: Store<any>) {
    super(injector, store);
  }

  //Core Nuevo (Backend-Services)

  addSortToQuery(query: string, sort: { field: string; order: string }) {
    let newQuery = '';
    if (query.includes('sortField')) {
      newQuery = query.split('&sortField')[0];
    }
    query = newQuery + `&sortField=${sort.field}&sortOrder=${sort.order}`;
    return query;
  }

  getUsers(page: number, limit: number, query?: any) {
    let params = new HttpParams().set('page', page).set('rowsPerPage', limit);
    if (query?.name) params = params.set('name', query.name);
    if (query?.email) params = params.set('email', query.email);
    if (query?.roles) params = params.set('roles', query.roles);
    if (query?.sortField) params = params.set('sortField', query.sortField);
    if (query?.sortOrder) params = params.set('sortOrder', query.sortOrder);
    const unitId = this.getProprietorUnitId() ?? query?.unitId;
    if (unitId) params = params.set('unitId', unitId);

    return this.http.get(`${environment.apiUrl}/admin/v1/users`, { params: params });
  }

  createUser(payload: PayloadUpdateUser) {
    return this.http.post(`${environment.apiUrl}/admin/v1/users`, payload);
  }

  createUserWithBiometric(formData: FormData) {
    return this.http.post(`${environment.newApiBackend}/api/user/admin/create`, formData);
  }

  createUserWithBiometricByProprietor(formData: FormData) {
    return this.http.post(`${environment.newApiBackend}/api/user/proprietor/create`, formData);
  }

  updateUserInfo(userId: string, payload: PayloadUpdateUser) {
    return this.http.put(`${environment.apiUrl}/admin/v1/users/${userId}`, payload);
  }

  updateUserStatusByAdmin(userId: string, buildingId: string, unitId: string, status: UserStatus) {
    return this.http.patch(`${environment.newApiBackend}/api/user-building/admin/status`, { userId, buildingId, unitId, status });
  }

  updateUserStatusByProprietor(userId: string, buildingId: string, unitId: string, status: UserStatus) {
    return this.http.patch( `${environment.newApiBackend}/api/user-building/proprietor/status`, { userId, buildingId, unitId, status });
  }

  deleteUserRelationshipByAdmin(userId: string, buildingId: string, unitId: string) {
    return this.http.patch(
      `${environment.newApiBackend}/api/user-building/admin/delete`,
      { userId, buildingId, unitId });
  }

  deleteUserRelationshipByProprietor(userId: string, buildingId: string, unitId: string) {
    return this.http.patch(
      `${environment.newApiBackend}/api/user-building/proprietor/delete`,
      { userId, buildingId, unitId });
  }

  deactivateUser(userId: string, buildingId?: string, unitId?: string) {
    if (buildingId && unitId) {
      return this.isProprietorOnly()
        ? this.updateUserStatusByProprietor(userId, buildingId, unitId, UserStatus.inactive)
        : this.updateUserStatusByAdmin(userId, buildingId, unitId, UserStatus.inactive);
    }
    return this.http.patch(`${environment.apiUrl}/admin/v1/users/${userId}/status`, { status: UserStatus.inactive });
  }

  removeUser(userId: string) {
    return this.http.delete(`${environment.apiUrl}/admin/v1/users/${userId}`);
  }

  deleteUser(userId: string, buildingId: string, unitId: string) {
    if (buildingId && unitId) {
      return this.isProprietorOnly()
        ? this.deleteUserRelationshipByProprietor(userId, buildingId, unitId)
        : this.deleteUserRelationshipByAdmin(userId, buildingId, unitId);
    }
  }

  activateUser(userId: string, buildingId?: string, unitId?: string) {
    if (buildingId && unitId) {
      return this.isProprietorOnly()
        ? this.updateUserStatusByProprietor(userId, buildingId, unitId, UserStatus.active)
        : this.updateUserStatusByAdmin(userId, buildingId, unitId, UserStatus.active);
    }
    return this.http.patch(`${environment.apiUrl}/admin/v1/users/${userId}/status`, { status: UserStatus.active });
  }

  uploadUsersFromXSLX(payload: ExcelInfoToUpload) {
    return this.http.post(`${environment.apiUrl}/admin/v1/users/upload`, payload);
  }

  sendDbUserForAdmin() {
    return this.http.get(`${environment.apiUrl}/admin/v1/users/download`);
  }

  /** Descarga DB de usuarios scoped a la unidad del propietario (VDS-3386). */
  sendDbUserForProprietor(unitId: string) {
    const params = new HttpParams().set('unitId', unitId);
    return this.http.get(`${environment.apiUrl}/proprietor/v1/users/download`, { params });
  }

  //Core Nuevo (Backend-Services)

  getUnitsForSearchByBuildingId(id) {
    return this.http.get(`${environment.apiUrl}/super/v1/units/${id}/all`);
  }

  getUnitsByBuildingId(id, pageP?, rowsPerP?, search?, status?: string) {
    search = search ? `&unit=${search}` : '';
    const page = pageP ? pageP : this.page;
    const rowsPerPage = rowsPerP ? rowsPerP : 10;

    const isStatus = status ? `&status=${status}` : '';

    return this.http.get(`${environment.apiUrl}/super/v1/units/${id}?page=${page}&rowsPerPage=${rowsPerPage}${search}${isStatus}`);
  }

  getUnitsByBuildingIdV2(buildingId: string, params: HttpParams) {
    return this.http.get(`${environment.apiUrl}/super/v1/units/${buildingId}`, { params });
  }

  getInvitationsByBuilding(id, page?, size?, type?, search?, date?, unit?, insideBuild?) {
    page = page ? page : 1;
    size = size ? size : 12;
    type = type ? `&type=${type}` : '';
    search = search ? `&search=${search}` : '';
    unit = unit ? `&unit=${unit}` : '';
    const startDate = date?.startDate ? `&startDate=${date.startDate}` : '';
    const endDate = date?.endDate ? `&endDate=${date.endDate}` : '';
    const insideBuilding = insideBuild ? `&insideBuilding=${insideBuild}` : '';
    let sort = '';
    if (date && date.startDate && date.startDate) {
      sort = '&sortField=date&sortOrder=1';
    }
    return this.http.get(
      `${environment.apiUrl}/super/v1/invitations/${id}?size=${size}&page=${page}${type}${search}${startDate}${endDate}${unit}${insideBuilding}${sort}`,
      );
  }

  getReservationsByBuildingId(id, pageP?, unit?, area?, date?, search?) {
    const page = pageP ? pageP : this.page;
    const rowsPerPage = 6;
    unit = unit ? `&unit=${unit}` : '';
    area = area ? `&area=${area}` : '';
    let startDate = date ? `&startDate=${date.startDate}` : '';
    let endDate = date ? `&endDate=${date.endDate}` : '';
    search = search ? `&search=${search}` : '';
    return this.http.get(
      `${environment.apiUrl}/super/v1/reservations/${id}?page=${page}&rowsPerPage=${rowsPerPage}${unit}${area}${startDate}${endDate}${search}`,
      );
  }

  getReservationsByBuildingIdMacro(id, page = 1, query?) {
    let params = new HttpParams();
    params = params.set('page', page);
    return this.http.get(`${environment.apiUrl}/super/v1/reservations/${id}?${query}`, { params });
  }

  getTempUsers(buildingId: string, params: HttpParams) {
    return this.http.get(`${environment.apiUrl}/super/v1/tmp-users/${buildingId}`, { params });
  }

  fetchUsers(page, limit, search, find?) {
    const safePage = Number.isFinite(Number(page)) && Number(page) > 0 ? Number(page) : 1;
    const safeLimit = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : 10;
    const params = typeof search === 'string' && search.trim() ? `&${search}` : '';
    const safeFind = typeof find === 'string' ? find.trim() : '';
    find = safeFind ? `&search=${encodeURIComponent(safeFind)}` : '';
    return this.http
      .get(`${environment.apiUrl}/user?page=${safePage}&rowsPerPage=${safeLimit}${params}${find}`)
      .toPromise()
      .then((res) => {
        return res || [];
      })
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  fetchUsersSub(page, limit, search, find?) {
    const safePage = Number.isFinite(Number(page)) && Number(page) > 0 ? Number(page) : 1;
    const safeLimit = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : 10;
    const params = typeof search === 'string' && search.trim() ? `&${search}` : '';
    const safeFind = typeof find === 'string' ? find.trim() : '';
    find = safeFind ? `&search=${encodeURIComponent(safeFind)}` : '';
    return this.http.get(`${environment.apiUrl}/user?page=${safePage}&rowsPerPage=${safeLimit}${params}${find}`);
  }

  storeUser(data, role, status, building?) {
    const buildObject = building ? building : this.getUser().buildings[0];
    const _id = data.user.unit ? (data.user.unit._id ? data.user.unit._id : data.user.unit.id) : null;
    const number = data.user.unit ? data.user.unit.number : null;
    let unit = null;

    if (_id) unit = { _id };
    else unit = { number };

    delete data.user.unit;
    // delete data.user.apartmentId;
    data = {
      ...data,
      building: { _id: buildObject._id },
      roles: [role],
      status: status,
      unit };
    return this.http
      .post(`${environment.apiUrl}/user`, data)
      .toPromise()
      .then((res: any) => {
        return res.object;
      })
      .catch((err) => {
        console.log('errr: ', err);
        return Promise.reject(err);
      });
  }

  getUserProfile() {
    return this.http.get(`${environment.apiUrl}/profile/v1/info`);
  }

  updateAdminEmail(email: string) {
    return this.http.put(`${environment.apiUrl}/profile/v1/email`, { email });
  }

  updateAdminInfo(name: string, phoneDetail: PhoneDetail) {
    return this.http.put(`${environment.apiUrl}/profile/v1/info`, { name, phoneDetail });
  }

  updateUser(data, role, status) {
    delete data.user.apartmentNumber;
    delete data.user.apartmentId;

    data = {
      ...data,
      roles: [role],
      status: status };
    return this.http
      .put(`${environment.apiUrl}/user/${data.user.id}`, data)
      .toPromise()
      .then((res: any) => {
        return res;
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  fetchTmpUser(page, limit, status, search, advanceSearch?) {
    let params = new HttpParams();
    if (page) params = params.set('page', page);
    if (limit) params = params.set('rowsPerPage', limit);
    params = params.set('status', status);
    if (search !== '') params = params.set('search', search);
    advanceSearch = advanceSearch && advanceSearch !== '' ? advanceSearch : '';

    return this.http
      .get(`${environment.apiUrl}/admin/v1/tmp-users?${advanceSearch}`, { params: params })
      .toPromise()
      .then((res) => {
        return res || [];
      })
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  updateTmpUser(tmpUserId: string, payload: PayloadUpdateTmpUser) {
    return this.http
      .put(`${environment.apiUrl}/admin/v1/tmp-users/${tmpUserId}`, payload)
      .toPromise()
      .then((res: any) => {
        return res.object;
      })
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  deleteTmpUser(data) {
    return this.http
      .delete(`${environment.apiUrl}/admin/v1/tmp-users/${data.user.id}/deleted`, data)
      .toPromise()
      .then((res: any) => {
        return res.object;
      })
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  acceptTmpUser(data) {
    const tmpUserId = data.user.id;
    delete data.user;
    return this.http
      .patch(`${environment.apiUrl}/admin/v1/tmp-users/${tmpUserId}/accepted`, data)
      .toPromise()
      .then((res: any) => {
        return res.object;
      })
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  uploadPicture(base64: string, format: string) {
    return this.http.put(`${environment.apiUrl}/profile/v1/image`, { base64, format });
  }

  updateFcmToken(id, fcmToken) {
    return this.http
      .put(`${environment.apiUrl}/users/${id}/fcmToken`, { fcmToken })
      .toPromise()
      .then((res: any) => {
        return res.object;
      })
      .catch((err) => {
        console.log(err);
        return Promise.reject(err);
      });
  }

  invitationResponse(data: any, invitation_id: any) {
    return this.http.post(`${environment.apiUrl}/v2/invitation/${invitation_id}/update-status`, data);
  }

  // SERVICIOS PARA LOS SUPER USUARIOS
  getUserById(user_id: string) {
    return this.http.get(`${environment.apiUrl}/super/v1/users/${user_id}`);
  }

  getAllUsersBeingSuper(params: any) {
    let parameters = new HttpParams().set('email', params.email);
    !params.building ? null : (parameters = parameters.set('buildingId', params.building));
    !params.unit ? null : (parameters = parameters.set('unitId', params.unit));
    parameters = parameters.set('page', params.page);
    parameters = parameters.set('rowsPerPage', params.limit);
    return this.http.get(`${environment.apiUrl}/super/v1/users`, { params: parameters });
  }

  getRols() {
    return this.http.get(`${environment.apiUrl}/super/v1/users/user-roles`);
  }

  createUserBeingSuper(user: any) {
    return this.http.post(`${environment.apiUrl}/super/v1/users`, user);
  }

  updateUserBeingSuper(user: any) {
    return this.http.put(`${environment.apiUrl}/super/v1/users/${user.id}`, user);
  }

  updateUserBuilding(user_id: string, building_data: any) {
    return this.http.put(`${environment.apiUrl}/super/v1/users/${user_id}/update-buildings`, building_data);
  }

  updateUserEmail(user_id: string, user_data: any) {
    return this.http.put(`${environment.apiUrl}/super/v1/users/${user_id}/update-email`, { email: user_data.email });
  }

  updateUserNotification(user_id: string, notifications_data: any) {
    return this.http.put(`${environment.apiUrl}/super/v1/users/${user_id}/update-notifications`, notifications_data);
  }

  updateUserPassword(user_id: string, password_data: any) {
    return this.http.put(`${environment.apiUrl}/super/v1/users/${user_id}/update-password`, password_data);
  }

  generateUserPassword(user_id: any) {
    return this.http.put(`${environment.apiUrl}/super/v1/users/${user_id}/generate-password`, {});
  }

  updateAccess(user_id: string, status: any) {
    return this.http.put(`${environment.apiUrl}/super/v1/users/${user_id}/change-user-status`, status);
  }

  createBuildingOfUSer(data: any) {
    return this.http.post(`${environment.apiUrl}/super/v1/users/new-buildings`, data);
  }

  deleteBuildingOfUSer(building_id: string) {
    return this.http.delete(`${environment.apiUrl}/super/v1/users/${building_id}/delete-buildings`);
  }

  deleteUserOfBuilding(userId: string) {
    return this.http.delete(`${environment.apiUrl}/super/v1/users/${userId}`);
  }

  sendUserInformationToBuilding(building_id: string) {
    return this.http.get(`${environment.apiUrl}/super/v1/users/buildings/${building_id}`);
  }

  updateLanguage(language: string) {
    return this.http.patch(`${environment.apiUrl}/profile/v1/lang`, { lang: language });
  }

  updatePasswordByUserId(userId: string, payload: { newPassword: string }) {
    return this.http.patch(`${environment.apiUrl}/admin/v1/users/${userId}/password`, payload);
  }

  generateUserPasswordAdmin(user_id: any) {
    return this.http.put(`${environment.apiUrl}/admin/v1/users/${user_id}/generate-password`, {});
  }

  getInvitationsByBuildingId(buildingId: string, params: HttpParams) {
    return this.http.get(
      `${environment.apiUrl}/super/v1/invitations/${buildingId}`,
      { params }
    );
  }

  getAllUsers(params: HttpParams) {
    return this.http.get(`${environment.apiUrl}/admin/v1/users`, { params: params });
  }

  getSuperAllUsers(params: HttpParams) {
    // El buildingId se envia por query params
    return this.http.get(`${environment.apiUrl}/super/v1/users`, { params: params });
  }

  // ADMIN - MONOREPO

  getAllUsersByAdmin(page: number, limit: number, query: GetUsersByAdminQuery) {
    let params = new HttpParams().set('page', page).set('rowsPerPage', limit);
    if (query.name) params = params.set('name', query.name);
    if (query.email) params = params.set('email', query.email);
    if (query.unitId) params = params.set('unitId', query.unitId);
    if (query.buildingId) params = params.set('buildingId', query.buildingId);
    if (query.roles) params = params.set('roles', query.roles);
    if (query.biometric !== undefined && query.biometric !== null) params = params.set('withBiometric', query.biometric);
    return this.http.get(`${environment.newApiBackend}/api/user/admin/all`, { params: params });

  }

  /**
   * Selector liviano de personas (pickers/filtros de tickets). Lee solo del
   * snapshot user-building populado en el backend: una query, sin findByIds.
   * Devuelve {id, name, email, photoUrl, roles, unit}. Usar este en lugar de
   * getAllUsersByAdmin para llenar dropdowns; el /all queda para la tabla de
   * gestión que necesita el payload completo.
   */
  getPeoplePickerByAdmin(page: number, limit: number, query: GetUsersByAdminQuery) {
    let params = new HttpParams().set('page', page).set('rowsPerPage', limit);
    if (query.name) params = params.set('name', query.name);
    if (query.unitId) params = params.set('unitId', query.unitId);
    if (query.buildingId) params = params.set('buildingId', query.buildingId);
    if (query.roles) params = params.set('roles', query.roles);
    return this.http.get(`${environment.newApiBackend}/api/user/admin/people-picker`, { params: params });
  }

  getAllUsersByProprietor(page: number, limit: number, query: GetUsersByAdminQuery) {
    let params = new HttpParams().set('page', page).set('rowsPerPage', limit);
    if (query.name) params = params.set('name', query.name);
    if (query.email) params = params.set('email', query.email);
    if (query.unitId) params = params.set('unitId', query.unitId);
    if (query.roles) params = params.set('roles', query.roles);
    if (query.biometric !== undefined && query.biometric !== null) params = params.set('withBiometric', query.biometric);
    return this.http.get(`${environment.newApiBackend}/api/user/proprietor/all`, { params: params }); 

  }

  updateUserNameByAdmin(userId: string, name: string) {
    return this.http.patch(`${environment.newApiBackend}/api/user/admin/name`, { userId, name });
  }

  updateUserNameByProprietor(userId: string, name: string) {
    return this.http.patch(`${environment.newApiBackend}/api/user/proprietor/name`, { userId, name });
  }

  updateUserEmailByAdmin(userId: string, email: string) {
    return this.http.patch(`${environment.newApiBackend}/api/user/admin/email`, { userId, email });
  }

  updateUserEmailByProprietor(userId: string, email: string) {
    return this.http.patch(`${environment.newApiBackend}/api/user/proprietor/email`, { userId, email });
  }

  updateUserPhoneByAdmin(userId: string, phoneDetail: PhoneDetail) {
    return this.http.patch(`${environment.newApiBackend}/api/user/admin/phone`, { userId, phoneDetail });
  }

  updateUserPhoneByProprietor(userId: string, phoneDetail: PhoneDetail) {
    return this.http.patch(`${environment.newApiBackend}/api/user/proprietor/phone`, { userId, phoneDetail });
  }

  updateUserPasswordByAdmin(userId: string, newPassword: string) {
    return this.http.patch(`${environment.newApiBackend}/api/user/admin/password`, { userId, newPassword });
  }

  updateUserPasswordByProprietor(userId: string, newPassword: string) {
    return this.http.patch(`${environment.newApiBackend}/api/user/proprietor/password`, { userId, newPassword });
  }

  requestPasswordRestartEmail(email: string) {
    const params = new HttpParams().set('email', email);
    return this.http.get(`${environment.newApiBackend}/api/user/restart-password`, { params });
  }

  updateUserRoleByAdmin(userId: string, buildingId: string, unitId: string, roles: string[]) {
    return this.http.patch(`${environment.newApiBackend}/api/user-building/admin/role`, { userId, buildingId, unitId, roles });
  }

  updateUserRoleByProprietor(userId: string, buildingId: string, unitId: string, roles: string[]) {
    return this.http.patch(`${environment.newApiBackend}/api/user-building/proprietor/role`, { userId, buildingId, unitId, roles });
  }

  updateUserUnit(userId: string, buildingId: string, oldUnitId: string, newUnitId: string) {
    return this.http.patch(`${environment.newApiBackend}/api/user-building/admin/unit`, { userId, buildingId, oldUnitId, newUnitId });
  }

  updateUserUnitWithLegacy(userId: string, userBuildingId: string, buildingId: string, unitId: string, roles: string[]) {
    const payload = {
      userBuildingId,
      building: buildingId,
      unit: unitId,
      roles: roles
    };
    return this.http.put(`${environment.apiUrl}/super/v1/users/${userId}/update-buildings`, payload);
  }

  updateOrCreatePersonBiometricData(formData: FormData, buildingId: string) {
    const headers = new HttpHeaders({ buildingId });
    return this.http.patch(`${environment.newApiBackend}/api/user/admin/person-biometric-data`, formData, { headers });
  }

  updateOrCreatePersonBiometricDataByProprietor(formData: FormData, buildingId: string) {
    const headers = new HttpHeaders({ buildingId });
    return this.http.patch(`${environment.newApiBackend}/api/user/proprietor/person-biometric-data`, formData, { headers });
  }

  updateUserFeaturesByAdmin(userId: string, buildingId: string, unitId: string, features: string[]) {
    return this.http.patch(`${environment.newApiBackend}/api/user-building/admin/features`, { userId, buildingId, unitId, features });
  }
}
